import React from 'react';
import { X, Calendar, TrendingUp, Clock, Edit2 } from 'lucide-react';
interface Project {
  id: number;
  title: string;
  description: string;
  status: string;
  dueDate: string;
  progress: number;
}
interface ProjectDetailsModalProps {
  project: Project;
  onClose: () => void;
  onEdit: () => void;
}
export function ProjectDetailsModal({
  project,
  onClose,
  onEdit
}: ProjectDetailsModalProps) {
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Terminé':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'En cours':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Planifié':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const daysUntilDue = Math.ceil((new Date(project.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-id="element-46">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-47">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b p-6 flex justify-between items-start" data-id="element-48">
          <div className="flex-1" data-id="element-49">
            <h2 className="text-2xl font-semibold mb-2" data-id="element-50">{project.title}</h2>
            <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(project.status)}`} data-id="element-51">
              {project.status}
            </span>
          </div>
          <div className="flex gap-2" data-id="element-52">
            <button onClick={onEdit} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors" title="Modifier" data-id="element-53">
              <Edit2 size={20} data-id="element-54" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors" data-id="element-55">
              <X size={24} data-id="element-56" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-6" data-id="element-57">
          {/* Description */}
          <div data-id="element-58">
            <h3 className="text-lg font-semibold mb-2" data-id="element-59">Description</h3>
            <p className="text-muted-foreground" data-id="element-60">{project.description}</p>
          </div>
          {/* Progress */}
          <div data-id="element-61">
            <div className="flex justify-between items-center mb-2" data-id="element-62">
              <h3 className="text-lg font-semibold flex items-center gap-2" data-id="element-63">
                <TrendingUp size={20} data-id="element-64" />
                Progression
              </h3>
              <span className="text-2xl font-bold" data-id="element-65">{project.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4" data-id="element-66">
              <div className="bg-blue-600 h-4 rounded-full transition-all duration-500" style={{
              width: `${project.progress}%`
            }} data-id="element-67"></div>
            </div>
          </div>
          {/* Timeline Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-68">
            <div className="border rounded-lg p-4" data-id="element-69">
              <div className="flex items-center gap-2 text-muted-foreground mb-2" data-id="element-70">
                <Calendar size={18} data-id="element-71" />
                <span className="text-sm font-medium" data-id="element-72">Date d'échéance</span>
              </div>
              <p className="text-lg font-semibold" data-id="element-73">
                {formatDate(project.dueDate)}
              </p>
            </div>
            <div className="border rounded-lg p-4" data-id="element-74">
              <div className="flex items-center gap-2 text-muted-foreground mb-2" data-id="element-75">
                <Clock size={18} data-id="element-76" />
                <span className="text-sm font-medium" data-id="element-77">Temps restant</span>
              </div>
              <p className="text-lg font-semibold" data-id="element-78">
                {daysUntilDue > 0 ? `${daysUntilDue} jours` : daysUntilDue === 0 ? "Aujourd'hui" : 'En retard'}
              </p>
            </div>
          </div>
          {/* Additional Details Section */}
          <div data-id="element-79">
            <h3 className="text-lg font-semibold mb-3" data-id="element-80">
              Détails supplémentaires
            </h3>
            <div className="space-y-2 text-sm" data-id="element-81">
              <div className="flex justify-between py-2 border-b" data-id="element-82">
                <span className="text-muted-foreground" data-id="element-83">ID du projet</span>
                <span className="font-medium" data-id="element-84">#{project.id}</span>
              </div>
              <div className="flex justify-between py-2 border-b" data-id="element-85">
                <span className="text-muted-foreground" data-id="element-86">Statut actuel</span>
                <span className="font-medium" data-id="element-87">{project.status}</span>
              </div>
              <div className="flex justify-between py-2" data-id="element-88">
                <span className="text-muted-foreground" data-id="element-89">
                  Dernière mise à jour
                </span>
                <span className="font-medium" data-id="element-90">
                  {formatDate(new Date().toISOString().split('T')[0])}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t p-6 flex justify-end gap-3" data-id="element-91">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-92">
            Fermer
          </button>
          <button onClick={onEdit} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors" data-id="element-93">
            Modifier le projet
          </button>
        </div>
      </div>
    </div>;
}